import React, {useMemo, useState} from "react";
import {Container} from "react-bootstrap";
import {Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {formatNumber, formatPercent, GROUP_PALETTE} from "../../Components/Statistics/format.js";
import {bereichFarbe, formatDuration} from "../../Components/Nutzung/format.js";
import {BereichLink, MitarbeiterLink} from "../../Components/Nutzung/Links.jsx";
import NutzungMitarbeiterAuswahl from "../../Components/Nutzung/NutzungMitarbeiterAuswahl.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";

/** Stabile Farbe je Person über ALLE Kennzahl-Karten hinweg - Reihenfolge
 * der Auswahl entscheidet, nicht die Antwortreihenfolge, damit eine Person
 * ihre Farbe nicht wechselt, wenn sich die Auswahl der anderen ändert. */
function personenFarben(ausgewaehlt) {
    return Object.fromEntries(ausgewaehlt.map((id, index) => [id, GROUP_PALETTE[index % GROUP_PALETTE.length]]));
}

/** Eine Kennzahl als waagerechte Balken - eine Zeile je ausgewählter Person,
 * in der Farbe dieser Person. Getrennte Kacheln je Kennzahl statt eines
 * gemeinsamen Diagramms, weil Sekunden (aktive Zeit/Anwesenheit) und
 * Stückzahlen (Ereignisse/Änderungen) auf derselben Achse nicht vergleichbar
 * wären. */
function VergleichsKennzahl({title, help, mitarbeiter, feld, formatWert, farben}) {
    const rows = useMemo(
        () => [...mitarbeiter].sort((a, b) => (b[feld] || 0) - (a[feld] || 0)),
        [mitarbeiter, feld]
    );
    const height = Math.max(120, rows.length * 42);

    return (
        <NutzungWidget title={title} help={help} loading={false} error={null} empty={rows.length === 0} height={height}>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={rows} layout="vertical" margin={{top: 4, right: 32, left: 8, bottom: 4}}>
                    <XAxis type="number" tickFormatter={formatWert} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={140} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(value) => formatWert(value)} labelFormatter={(label) => label} />
                    <Bar dataKey={feld} radius={[0, 6, 6, 0]} barSize={18}>
                        {rows.map((row) => (
                            <Cell key={row.user_id} fill={farben[row.user_id] || GROUP_PALETTE[0]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </NutzungWidget>
    );
}

/** Bereichsverteilung einer einzelnen Person als schmaler gestapelter
 * Balken - kompakt genug, um mehrere Personen nebeneinander zu zeigen. */
function PersonBereichsbalken({person}) {
    const rows = useMemo(
        () => [...(person.bereiche || [])].filter((eintrag) => (eintrag.anteil || 0) > 0).sort((a, b) => (b.anteil || 0) - (a.anteil || 0)),
        [person.bereiche]
    );

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <div className="fw-semibold mb-2">
                    <MitarbeiterLink id={person.user_id} name={person.name} />
                </div>
                {rows.length === 0 ? (
                    <div className="text-muted small">Keine Ereignisse im Zeitraum.</div>
                ) : (
                    <>
                        <div className="d-flex rounded-2 overflow-hidden mb-2" style={{height: 16}}>
                            {rows.map((eintrag) => (
                                <div
                                    key={eintrag.bereich}
                                    style={{width: `${Math.max((eintrag.anteil || 0) * 100, 1)}%`, background: bereichFarbe(eintrag.bereich)}}
                                />
                            ))}
                        </div>
                        <div className="d-flex flex-column gap-1 small">
                            {rows.map((eintrag) => (
                                <div key={eintrag.bereich} className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <span
                                            aria-hidden="true"
                                            style={{width: 9, height: 9, borderRadius: 2, background: bereichFarbe(eintrag.bereich), display: "inline-block"}}
                                        />
                                        <BereichLink bereich={eintrag.bereich} />
                                    </span>
                                    <span className="text-muted">{formatPercent((eintrag.anteil || 0) * 100, 0)}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Gegenüberstellung mehrerer Mitarbeiter - eigene Mehrfachauswahl
 * (NutzungMitarbeiterAuswahl, kein natives <select multiple>), danach vier
 * Kennzahl-Kacheln nebeneinander sowie je Person die Bereichsverteilung.
 * Ohne Auswahl steht dort ausdrücklich eine Aufforderung statt der Kachel -
 * kein "leerer Baustein", sondern eine bewusste Handlungsaufforderung
 * (Spezifikation, Auftrag Punkt 4).
 */
export default function NutzungVergleich() {
    const nutzungFilters = useNutzungFilter();
    const {filters, employeeOptions} = nutzungFilters;
    const [ausgewaehlt, setAusgewaehlt] = useState([]);

    const vergleich = useStatisticsData(
        "/nutzung/daten/vergleich",
        {...filters, user_ids: ausgewaehlt},
        {skip: ausgewaehlt.length === 0}
    );

    const mitarbeiter = useMemo(() => {
        const rows = Array.isArray(vergleich.data?.mitarbeiter) ? vergleich.data.mitarbeiter : [];
        return rows.map((row) => ({...row, name: row.name || `Nutzer ${row.user_id}`}));
    }, [vergleich.data]);

    const farben = useMemo(() => personenFarben(ausgewaehlt), [ausgewaehlt]);

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Mitarbeiter" titel="Vergleich" nutzungFilters={nutzungFilters} showMitarbeiter={false}>
                <div className="d-flex flex-column gap-3">
                    <NutzungMitarbeiterAuswahl options={employeeOptions} selected={ausgewaehlt} onChange={setAusgewaehlt} />

                    {ausgewaehlt.length === 0 && (
                        <div className="alert alert-secondary mb-0" role="status">
                            Bitte oben mindestens einen Mitarbeiter auswählen, um ihn mit anderen zu vergleichen.
                        </div>
                    )}

                    {ausgewaehlt.length > 0 && vergleich.error && (
                        <div className="alert alert-danger mb-0">{vergleich.error}</div>
                    )}

                    {ausgewaehlt.length > 0 && !vergleich.loading && !vergleich.error && (
                        <>
                                    <div className="row g-3">
                                        <div className="col-lg-6">
                                            <VergleichsKennzahl
                                                title="Aktive Zeit"
                                                help="Aufsummierte aktive Zeit im gewählten Zeitraum je Person."
                                                mitarbeiter={mitarbeiter}
                                                feld="aktiv_sekunden"
                                                formatWert={formatDuration}
                                                farben={farben}
                                            />
                                        </div>
                                        <div className="col-lg-6">
                                            <VergleichsKennzahl
                                                title="Anwesenheit"
                                                help="Aufsummierte Anwesenheit (Fenster geöffnet) im gewählten Zeitraum je Person."
                                                mitarbeiter={mitarbeiter}
                                                feld="anwesend_sekunden"
                                                formatWert={formatDuration}
                                                farben={farben}
                                            />
                                        </div>
                                        <div className="col-lg-6">
                                            <VergleichsKennzahl
                                                title="Ereignisse"
                                                help="Anzahl aller erfassten Ereignisse im gewählten Zeitraum je Person."
                                                mitarbeiter={mitarbeiter}
                                                feld="ereignisse"
                                                formatWert={formatNumber}
                                                farben={farben}
                                            />
                                        </div>
                                        <div className="col-lg-6">
                                            <VergleichsKennzahl
                                                title="Änderungen"
                                                help="Anzahl der Datenänderungen im gewählten Zeitraum je Person."
                                                mitarbeiter={mitarbeiter}
                                                feld="aenderungen"
                                                formatWert={formatNumber}
                                                farben={farben}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <h2 className="h6 text-muted mb-2">Bereichsverteilung je Person</h2>
                                        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
                                            {mitarbeiter.map((person) => (
                                                <div className="col" key={person.user_id}>
                                                    <PersonBereichsbalken person={person} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                        </>
                    )}
                </div>
            </NutzungLayout>
        </Container>
    );
}
